import { useRef } from 'react';
import { useRecoilCallback } from 'recoil';
import { atom_routeToStopLocation, atom_currentNavigationStep } from '@app/shared/state/tour.atom';
import polyline from '@mapbox/polyline';

// Mode-based step advance thresholds (in meters)
const STEP_THRESHOLDS = {
	walking: 20,
	bicycling: 20,
	driving: 35,
	transit: 50,
} as const;

function getStepThreshold(travelMode?: 'transit' | 'walking' | 'bicycling' | 'driving'): number {
	if (!travelMode) return STEP_THRESHOLDS.transit; // default to transit
	return STEP_THRESHOLDS[travelMode] ?? STEP_THRESHOLDS.transit;
}

// Haversine distance in meters
function calculateDistance(
	c1: { lat: number; lng: number },
	c2: { lat: number; lng: number }
): number {
	const R = 6371e3;
	const lat1 = (c1.lat * Math.PI) / 180;
	const lat2 = (c2.lat * Math.PI) / 180;
	const dLat = ((c2.lat - c1.lat) * Math.PI) / 180;
	const dLng = ((c2.lng - c1.lng) * Math.PI) / 180;

	const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
	return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Distance from point to line segment
function distanceToSegment(
	point: { lat: number; lng: number },
	segStart: [number, number],
	segEnd: [number, number]
): number {
	const a = { lat: segStart[0], lng: segStart[1] };
	const b = { lat: segEnd[0], lng: segEnd[1] };
	const ab = { lat: b.lat - a.lat, lng: b.lng - a.lng };
	const ap = { lat: point.lat - a.lat, lng: point.lng - a.lng };
	const abSquared = ab.lat * ab.lat + ab.lng * ab.lng;
	if (abSquared === 0) return calculateDistance(point, a);
	const t = Math.max(0, Math.min(1, (ap.lat * ab.lat + ap.lng * ab.lng) / abSquared));
	return calculateDistance(point, { lat: a.lat + t * ab.lat, lng: a.lng + t * ab.lng });
}

// Find closest step index based on distance to polyline
// Uses <= so that at step boundaries (shared endpoint), the later step is preferred
function findClosestStepIndex(userLocation: { lat: number; lng: number }, steps: any[]): { index: number; distance: number } {
	let minDistance = Infinity;
	let closestIndex = 0;
	steps.forEach((step, i) => {
		if (!step.polyline?.points) return;
		const coords = polyline.decode(step.polyline.points);
		for (let j = 0; j < coords.length - 1; j++) {
			const dist = distanceToSegment(userLocation, coords[j], coords[j + 1]);
			if (dist <= minDistance) {
				minDistance = dist;
				closestIndex = i;
			}
		}
	});
	return { index: closestIndex, distance: minDistance };
}

// Check if step instruction contains a pattern (case-insensitive, strips HTML)
function stepInstructionContains(step: any, pattern: RegExp): boolean {
	const instruction = (step?.html_instructions || '').replace(/<[^>]*>/g, '').toLowerCase();
	return pattern.test(instruction);
}

// Multi-language patterns for step detection
// Head: en=head, fr=dirigez-vous/prenez, he=פנה/צא, pt=siga, es=dirígete, de=fahren, it=procedi
const headPattern = /^(head|dirigez-vous|prenez|siga|dirígete|fahren|procedi)|^פנה|^צא/;
// Turn: en=turn, fr=tournez/tourner/tournera/prendre, he=פנה/פניה, pt=vire, es=gire, de=abbiegen, it=svolta
const turnPattern = /\b(turn|tournez|tourner|tournera|prendre|vire|gire|abbiegen|svolta)\b|פנה|פניה/;
// Straight: en=go straight/keep straight/continue straight/straight, fr=tout droit/continuez/continuer, he=ישר/המשך, pt=reto/em frente/continue
const straightPattern = /\b(go\s+straight|keep\s+straight|continue\s+straight|straight|tout\s+droit|continuez|continuer|reto|em\s+frente|continue|recto|siga\s+recto|geradeaus|weiter\s+geradeaus|dritto|continua\s+dritto)\b|ישר|המשך\s+ישר/;

// Check if we should skip the next step (for non-transit: "head" followed by "turn")
function shouldDoubleAdvance(steps: any[], currentIndex: number, travelMode?: string): boolean {
	// Only for non-transit modes
	if (travelMode === 'transit' || !travelMode) return false;

	// Only when advancing from step 0
	if (currentIndex !== 0) return false;

	// Check if step 0 is "head" and step 1 is "turn"
	if (steps.length < 2) return false;

	const isHeadStep = stepInstructionContains(steps[0], headPattern);
	const isTurnStep = stepInstructionContains(steps[1], turnPattern);

	return isHeadStep && isTurnStep;
}

// Check if current step is a "turn" that follows a "go/keep straight" step
function isTurnAfterStraight(steps: any[], currentIndex: number): boolean {
	if (currentIndex < 1) return false;

	const prevStep = steps[currentIndex - 1];
	const currentStep = steps[currentIndex];

	const isStraightStep = stepInstructionContains(prevStep, straightPattern);
	const isTurnStep = stepInstructionContains(currentStep, turnPattern);

	return isStraightStep && isTurnStep;
}

// Find the best starting step by checking which steps the user has already passed
function findBestStartingStep(userLocation: { lat: number; lng: number }, steps: any[], threshold: number): { index: number; method: 'radius' | 'polyline'; distance: number } {
	// Use a larger threshold for initialization — after a refresh, GPS drift means
	// the user might be slightly farther from the step boundary than during live tracking
	const initThreshold = threshold * 1.5;

	// First, find the last step whose end_location the user is within threshold of
	// This means user has completed that step and should be on the next one
	let lastCompletedStep = -1;
	let lastCompletedDistance = 0;
	for (let i = 0; i < steps.length - 1; i++) {
		const endLocation = steps[i].end_location;
		if (endLocation) {
			const distance = calculateDistance(userLocation, { lat: endLocation.lat, lng: endLocation.lng });
			if (distance < initThreshold) {
				lastCompletedStep = i;
				lastCompletedDistance = distance;
			}
		}
	}

	// If user is within threshold of a step's end, they should be on the next step
	if (lastCompletedStep >= 0) {
		let resultIndex = Math.min(lastCompletedStep + 1, steps.length - 1);

		// Turn-to-turn skip: if we landed on a turn step and the next is also a turn,
		// the user already passed this turn — show the upcoming one
		if (resultIndex < steps.length - 1) {
			const isCurrentTurn = stepInstructionContains(steps[resultIndex], turnPattern);
			const isNextTurn = stepInstructionContains(steps[resultIndex + 1], turnPattern);
			if (isCurrentTurn && isNextTurn) {
				resultIndex = resultIndex + 1;
			}
		}

		return {
			index: resultIndex,
			method: 'radius',
			distance: lastCompletedDistance
		};
	}

	// Otherwise, find the closest step by polyline distance
	const { index: closestIndex, distance: polylineDistance } = findClosestStepIndex(userLocation, steps);

	// Forward-progress check: if the user is closer to the END of the found step
	// than to its START, they may have already passed it. Check if they should be
	// on the next step instead.
	if (closestIndex < steps.length - 1) {
		const currentStep = steps[closestIndex];
		if (currentStep.start_location && currentStep.end_location) {
			const distToStart = calculateDistance(userLocation, {
				lat: currentStep.start_location.lat,
				lng: currentStep.start_location.lng
			});
			const distToEnd = calculateDistance(userLocation, {
				lat: currentStep.end_location.lat,
				lng: currentStep.end_location.lng
			});
			// User is past the midpoint of this step — check if they're actually on the next step
			if (distToEnd < distToStart) {
				const nextStep = steps[closestIndex + 1];
				if (nextStep.start_location) {
					const distToNextStart = calculateDistance(userLocation, {
						lat: nextStep.start_location.lat,
						lng: nextStep.start_location.lng
					});
					// If user is close to next step's start, they've moved on to it
					if (distToNextStart < initThreshold) {
						return {
							index: closestIndex + 1,
							method: 'polyline',
							distance: distToNextStart
						};
					}
				}
			}
		}
	}

	// Turn-to-turn skip: if the closest polyline belongs to a turn step,
	// the user is on the straight segment AFTER the turn (already passed it).
	// If the next step is also a turn, show that upcoming turn instead.
	let finalIndex = closestIndex;
	if (finalIndex < steps.length - 1) {
		const isCurrentTurn = stepInstructionContains(steps[finalIndex], turnPattern);
		const isNextTurn = stepInstructionContains(steps[finalIndex + 1], turnPattern);
		if (isCurrentTurn && isNextTurn) {
			finalIndex = finalIndex + 1;
		}
	}

	return {
		index: finalIndex,
		method: 'polyline',
		distance: polylineDistance
	};
}

/**
 * Hook to track user's current navigation step
 * Returns checkStepProgression callback to be called when user moves 30m
 * Only processes if routeToStopLocation exists
 */
export function useNavigationStepTracker() {
	const hasInitializedRef = useRef(false);
	const previousRouteIdRef = useRef<string | null>(null);
	const insideTurnRadiusRef = useRef(false); // Track if user is inside a turn step's start radius

	// Check step progression - called by GeolocationProvider every 30m movement
	const checkStepProgression = useRecoilCallback(({ snapshot, set }) => (
		userLocation: { lat: number; lng: number }
	) => {
		// Read fresh route state
		const routeLoadable = snapshot.getLoadable(atom_routeToStopLocation);
		const route = routeLoadable.state === 'hasValue' ? routeLoadable.contents : null;

		// Only run if route exists
		if (!route) {
			hasInitializedRef.current = false;
			previousRouteIdRef.current = null;
			return;
		}

		const firstLeg = route.legs?.[0];
		const steps = firstLeg?.steps;
		if (!steps || steps.length === 0) return;

		// Create a simple route identifier
		const routeId = route.overview_polyline?.points || '';

		// Check if route changed (new navigation started)
		if (previousRouteIdRef.current !== routeId) {
			previousRouteIdRef.current = routeId;
			hasInitializedRef.current = false;
			insideTurnRadiusRef.current = false;
		}

		// Get threshold based on travel mode
		const threshold = getStepThreshold(route.travelMode);

		// Initialize: find the best starting step based on user location
		if (!hasInitializedRef.current) {
			const result = findBestStartingStep(userLocation, steps, threshold);
			set(atom_currentNavigationStep, result.index);
			hasInitializedRef.current = true;
			return;
		}

		// Check step progression
		const stepLoadable = snapshot.getLoadable(atom_currentNavigationStep);
		const currentStepIndex = stepLoadable.state === 'hasValue' ? stepLoadable.contents : 0;

		if (currentStepIndex >= steps.length - 1) return; // Already on last step

		const step = steps[currentStepIndex];
		const endLocation = step.end_location;
		const startLocation = step.start_location;
		if (!endLocation) return;

		const distanceToEnd = calculateDistance(userLocation, { lat: endLocation.lat, lng: endLocation.lng });

		// Special handling for "turn" steps after "go/keep straight" steps (non-transit only)
		if (route.travelMode !== 'transit' && isTurnAfterStraight(steps, currentStepIndex) && startLocation) {
			const distanceToStart = calculateDistance(userLocation, { lat: startLocation.lat, lng: startLocation.lng });

			// Track entering the turn radius
			if (distanceToStart < threshold) {
				insideTurnRadiusRef.current = true;
			}

			// Advance when exiting the turn radius (was inside, now outside)
			if (insideTurnRadiusRef.current && distanceToStart >= threshold) {
				insideTurnRadiusRef.current = false;
				const nextStep = Math.min(currentStepIndex + 1, steps.length - 1);
				set(atom_currentNavigationStep, nextStep);
			}
			return;
		}

		// Default behavior: advance when within threshold of step's end location
		if (distanceToEnd < threshold) {
			// Check if we should double advance (non-transit: "head" + "turn")
			const advanceBy = shouldDoubleAdvance(steps, currentStepIndex, route.travelMode) ? 2 : 1;
			const nextStep = Math.min(currentStepIndex + advanceBy, steps.length - 1);
			set(atom_currentNavigationStep, nextStep);
		}
	}, []);

	return { checkStepProgression };
}
